<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue';
import { useCompanyStore } from '@Stores/company.store';
import { useLocationStore } from '@Stores/location.store';
import useFlowNavigation from '@Use/useFlowNavigation';
import useCompanyImageBaseUrl from '@Src/use/useCompanyImageBaseUrl';
import { useFlowSteps } from '@Use/useFlowSteps';
import useTaskNavTitle from '@Use/useTaskNavTitle';
import { finalizeSendRequestApi } from '@Api/requestFlow.api';
import { useRequestDetailsStore } from '@Stores/requestDetails.store';
import { useRequestSessionStore } from '@Stores/requestSession.store';
import { useMppPolicyLabel } from '@Use/useMppPolicyLabel';
import { trackBankInformationViewed } from '@Analytics/request.mixpanel';

// Import used components
import { FlowLayout } from '@Shared';

// Setup
const companyImageBaseUrl = useCompanyImageBaseUrl();
const companyStore = useCompanyStore();
const locationStore = useLocationStore();
const requestSessionStore = useRequestSessionStore();
const { advanceFlow, goBackInFlow, hasRouteInFlow, navigateToRoute } = useFlowNavigation();
const { getProgressSteps, getCurrentStep } = useFlowSteps();
const steps = getProgressSteps();
const requestDetailsStore = useRequestDetailsStore();
const { formatPolicyLabel } = useMppPolicyLabel();
const { taskNavTitle } = useTaskNavTitle();

// Page state
const isRequestDetailsLoading = ref<boolean>(true);
const isNavigating = ref(false);
const errorMessage = ref<string | null>(null);

// Computed
const mppPurchased = computed(() => requestDetailsStore.isMppPurchased);

const navigateToMpp = () => {
    const target =
        requestSessionStore.selectedMppPolicy && hasRouteInFlow('mpp-purchase-decision')
            ? 'mpp-purchase-decision'
            : 'mpp-transfer-amount';
    navigateToRoute(target);
};

// Actions
const continueToInstructions = async () => {
    isNavigating.value = true;
    await finalizeSendRequestApi();
    trackBankInformationViewed(
        requestDetailsStore.isMppPurchased,
        formatPolicyLabel(requestSessionStore.selectedMppPolicy),
    );
    advanceFlow();
};

// Methods
const fetchFullRequestDetails = async () => {
    const result = await requestDetailsStore.fetchRequestDetails();
    if (!result?.success) {
        requestDetailsStore.requestDetails = null;
        errorMessage.value = 'Something went wrong preparing your wire instructions. Please refresh and try again.';
    }
};

// Lifecycle
onMounted(async () => {
    isRequestDetailsLoading.value = true;
    await fetchFullRequestDetails();
    isRequestDetailsLoading.value = false;
});
</script>

<template>
    <flow-layout
        is-white-label
        :company="companyStore.company"
        :brand="companyStore.brand"
        :location="locationStore.location"
        :company-image-base-url="companyImageBaseUrl"
        :steps="steps"
        :current-step="getCurrentStep()"
        show-back-button
        @back-click="goBackInFlow"
    >
        <template v-if="taskNavTitle" #header>{{ taskNavTitle }}</template>

        <app-form-content-layout>
            <template v-if="isRequestDetailsLoading">
                <app-page-loader />
            </template>

            <template v-else-if="errorMessage">
                <p class="wire-instructions-ready__error" role="alert">
                    {{ errorMessage }}
                </p>
            </template>

            <template v-else>
                <h1 bottomOffset="1rem">Your verified instructions are ready</h1>
    
                <p>
                    By continuing, you agree to use only these verified instructions
                    for your wire transfer.
                </p>
    
                <app-button
                    expanded
                    size="large"
                    :is-loading="isNavigating"
                    data-id="wire-instructions-ready-continue"
                    @onClick="continueToInstructions"
                >
                    Continue to instructions
                </app-button>
    
                <div
                    v-if="mppPurchased"
                    class="wire-instructions-ready__disclaimer"
                >
                    <div class="wire-instructions-ready__disclaimer-header">
                        <app-icon icon="cd-ic-money-shield" class="wire-instructions-ready__disclaimer-icon" />
                        <span class="wire-instructions-ready__disclaimer-title">MONEY PROTECTION PLAN</span>
                    </div>
                    <p class="wire-instructions-ready__disclaimer-text">
                        Thank you for purchasing CertifID's Money Protection Plan.
                        We sent details to your email.
                    </p>
                </div>
    
                <div 
                    v-else-if="hasRouteInFlow('mpp-transfer-amount')"
                    class="wire-instructions-ready__centered-content">
                    <app-link
                        emphasized
                        type="internal"
                        data-id="wire-instructions-ready-mpp-link"
                        @onClick="navigateToMpp"
                    >
                        Interested in our Money Protection Plan?
                    </app-link>
                </div>
            </template>

        </app-form-content-layout>
    </flow-layout>
</template>

<style lang="scss" scoped>
@use "certifid-v2-client-hub-styles" as *;

.wire-instructions-ready {
    &__error {
        font-size: 0.875rem;
        color: $red;
        margin: 0;
    }

    &__disclaimer {
        background: $white;
        border: 1px solid $dark-gray;
        border-radius: 0.375rem;
        padding: 1rem;
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
    }

    &__disclaimer-header {
        display: flex;
        align-items: center;
        gap: 0.5rem;
    }

    &__disclaimer-icon {
        color: $green;
        font-size: 1.5rem;
    }

    &__disclaimer-title {
        font-size: 0.9rem;
        font-weight: 700;
        color: $black-med;
        line-height: 150%;
    }

    &__disclaimer-text {
        font-size: 0.9rem;
        line-height: 150%;
        color: $black-med;
        margin: 0;
    }

    &__centered-content {
        display: flex;
        justify-content: center;
    }
}
</style>
